import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, Plus, Trash2, ChevronRight, ChevronDown, Users2, Edit3, Network } from "lucide-react";

export default function Organizations() {
  const { user } = useAuth();
  const [tree, setTree] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(null);
  const [form, setForm] = useState({ name: "", parent_id: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [membersFor, setMembersFor] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/organizations/tree");
      setTree(data);
      // auto-expand roots
      const e = {}; data.forEach(n => { e[n.id] = true; }); setExpanded(prev => ({ ...prev, ...e }));
    } catch { toast.error("Failed to load organisations"); }
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const flat = (nodes, depth = 0, out = []) => {
    for (const n of nodes) { out.push({ ...n, depth }); flat(n.children || [], depth + 1, out); }
    return out;
  };
  const flatOptions = flat(tree);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      if (openEdit) {
        await api.patch(`/organizations/${openEdit.id}`, { name: form.name, notes: form.notes });
        toast.success("Organisation updated");
      } else {
        await api.post("/organizations", { name: form.name, parent_id: form.parent_id || null, notes: form.notes });
        toast.success("Organisation created");
      }
      setOpenCreate(false); setOpenEdit(null); setForm({ name: "", parent_id: "", notes: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  const remove = async (o) => {
    if (!confirm(`Delete "${o.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/organizations/${o.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const startCreate = (parent = null) => {
    setOpenEdit(null); setForm({ name: "", parent_id: parent?.id || "", notes: "" }); setOpenCreate(true);
  };
  const startEdit = (o) => {
    setOpenEdit(o); setForm({ name: o.name, parent_id: o.parent_id || "", notes: o.notes || "" }); setOpenCreate(true);
  };
  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const renderNode = (n) => {
    const kids = n.children || [];
    const open = expanded[n.id];
    return (
      <div key={n.id} className="border-b border-gray-100 last:border-b-0" data-testid={`org-node-${n.id}`}>
        <div className="flex items-center gap-2 py-3 px-3 hover:bg-gray-50" style={{ paddingLeft: `${(n.depth || 0) * 24 + 12}px` }}>
          <button onClick={() => toggle(n.id)} className={`w-5 h-5 flex items-center justify-center text-gray-500 ${kids.length === 0 ? "opacity-30" : "hover:text-gray-900"}`} data-testid={`org-toggle-${n.id}`}>
            {kids.length > 0 ? (open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : <div className="w-1 h-1 bg-gray-300 rounded-full" />}
          </button>
          <div className={`w-8 h-8 flex items-center justify-center ${n.depth === 0 ? "bg-[#01ADEF]" : "bg-gray-700"}`}>
            {n.depth === 0 ? <Building2 className="w-4 h-4 text-white" strokeWidth={2.5} /> : <Network className="w-4 h-4 text-white" strokeWidth={2.5} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate">{n.name}</div>
            {n.notes && <div className="text-xs text-gray-500 truncate">{n.notes}</div>}
          </div>
          <div className="text-[10px] font-mono font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1">
            <Users2 className="w-3 h-3" /> {n.customer_count || 0}
          </div>
          <button data-testid={`org-add-child-${n.id}`} onClick={() => startCreate(n)} title="Add sub-org" className="w-8 h-8 flex items-center justify-center hover:bg-white border border-transparent hover:border-gray-200 text-gray-500 hover:text-[#01ADEF]"><Plus className="w-4 h-4" /></button>
          <button data-testid={`org-members-${n.id}`} onClick={() => setMembersFor(n)} title="Assign customers & inspectors" className="w-8 h-8 flex items-center justify-center hover:bg-white border border-transparent hover:border-gray-200 text-gray-500 hover:text-[#01ADEF]"><Users2 className="w-4 h-4" /></button>
          <button data-testid={`org-edit-${n.id}`} onClick={() => startEdit(n)} title="Edit" className="w-8 h-8 flex items-center justify-center hover:bg-white border border-transparent hover:border-gray-200 text-gray-500"><Edit3 className="w-4 h-4" /></button>
          <button data-testid={`org-delete-${n.id}`} onClick={() => remove(n)} title="Delete" className="w-8 h-8 flex items-center justify-center hover:bg-white border border-transparent hover:border-gray-200 text-gray-400 hover:text-[#DC2626]"><Trash2 className="w-4 h-4" /></button>
        </div>
        {open && kids.length > 0 && <div>{kids.map(c => renderNode({ ...c, depth: (n.depth || 0) + 1 }))}</div>}
      </div>
    );
  };
  const rootsWithDepth = tree.map(t => ({ ...t, depth: 0 }));

  return (
    <div className="p-6 md:p-8" data-testid="organizations-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Structure</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Organisations</h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Manage your customer parent-companies and their sub-divisions. Assign each customer to an organisation and a sub-organisation to keep reporting and access clean.
          </p>
        </div>
        <Button data-testid="org-new-root-btn" onClick={() => startCreate(null)} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">
          <Plus className="w-4 h-4 mr-2" /> New Organisation
        </Button>
      </div>

      <div className="bg-white border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold tracking-tight">Hierarchy</h2>
          <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 font-mono">{rootsWithDepth.length} root · {flatOptions.length} total</span>
        </div>
        {tree.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-14 h-14 bg-gray-100 mx-auto flex items-center justify-center mb-3"><Building2 className="w-6 h-6 text-gray-400" strokeWidth={2} /></div>
            <div className="font-display text-lg font-bold tracking-tight">No organisations yet</div>
            <p className="text-sm text-gray-500 mt-1">Create a top-level organisation (like a parent company), then add sub-organisations (divisions, sites, departments).</p>
            <Button onClick={() => startCreate(null)} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6] mt-4"><Plus className="w-4 h-4 mr-2" />Create first organisation</Button>
          </div>
        ) : (
          <div data-testid="org-tree">{rootsWithDepth.map(n => renderNode(n))}</div>
        )}
      </div>

      <Dialog open={openCreate} onOpenChange={(v) => { setOpenCreate(v); if (!v) { setOpenEdit(null); } }}>
        <DialogContent className="rounded-md max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{openEdit ? "Edit organisation" : "New organisation"}</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              {openEdit ? "Update the name or notes." : "Leave the parent empty to create a top-level organisation, or pick a parent to add a sub-organisation."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs uppercase tracking-wider font-semibold">Name *</Label><Input data-testid="org-form-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Acme Group  or  Acme London Division" className="rounded-md mt-1" /></div>
            {!openEdit && (
              <div><Label className="text-xs uppercase tracking-wider font-semibold">Parent (optional)</Label>
                <Select value={form.parent_id || "none"} onValueChange={v => setForm({ ...form, parent_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="rounded-md mt-1" data-testid="org-form-parent"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None (top-level) —</SelectItem>
                    {flatOptions.map(o => <SelectItem key={o.id} value={o.id}>{"— ".repeat(o.depth)}{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div><Label className="text-xs uppercase tracking-wider font-semibold">Notes</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="rounded-md mt-1" placeholder="Optional details" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => { setOpenCreate(false); setOpenEdit(null); }}>Cancel</Button>
            <Button data-testid="org-save-btn" disabled={saving} onClick={submit} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">{saving ? "Saving…" : (openEdit ? "Update" : "Create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MembersDialog org={membersFor} onOpenChange={(v) => !v && setMembersFor(null)} onSaved={() => { setMembersFor(null); load(); }} />
    </div>
  );
}

function MembersDialog({ org, onOpenChange, onSaved }) {
  const [data, setData] = useState(null);
  const [custIds, setCustIds] = useState(new Set());
  const [inspIds, setInspIds] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!org) { setData(null); return; }
    setData(null);
    api.get(`/organizations/${org.id}/members`)
      .then(({ data }) => {
        setData(data);
        setCustIds(new Set(data.assigned.customer_ids));
        setInspIds(new Set(data.assigned.inspector_ids));
      })
      .catch(() => toast.error("Failed to load members"));
  }, [org]);

  const makeToggle = (setFn) => (id) => setFn(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const save = async () => {
    if (!org) return;
    setSaving(true);
    try {
      await api.patch(`/organizations/${org.id}/members`, { customer_ids: [...custIds], inspector_ids: [...inspIds] });
      toast.success("Members updated");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!org} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-lg" data-testid="org-members-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Users2 className="w-5 h-5 text-[#01ADEF]" />Assign members — {org?.name}</DialogTitle>
          <DialogDescription className="text-xs text-gray-500">Tick the customers and inspectors that belong to this {data?.is_sub ? "sub-organisation" : "organisation"}.</DialogDescription>
        </DialogHeader>
        {!data ? <div className="py-8 text-sm text-gray-500">Loading…</div> : (
          <div className="space-y-4 py-2 max-h-[55vh] overflow-y-auto">
            <MemberList title="Customers" testid="members-customers" items={data.candidates.customers} selected={custIds} onToggle={makeToggle(setCustIds)} />
            <MemberList title="Inspectors" testid="members-inspectors" items={data.candidates.inspectors} selected={inspIds} onToggle={makeToggle(setInspIds)} />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="org-members-save" disabled={saving || !data} onClick={save} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MemberList({ title, items, selected, onToggle, testid }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider font-semibold text-gray-600 mb-2">{title} <span className="text-gray-400">({selected.size}/{items.length})</span></div>
      {items.length === 0 ? <div className="text-xs text-gray-400">None in this tenant.</div> : (
        <div className="border border-gray-200 divide-y divide-gray-100" data-testid={testid}>
          {items.map(u => (
            <label key={u.id} data-testid={`member-${u.id}`} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" className="w-4 h-4" checked={selected.has(u.id)} onChange={() => onToggle(u.id)} data-testid={`member-toggle-${u.id}`} />
              <div className="min-w-0"><div className="text-sm font-semibold truncate">{u.name || u.email}</div><div className="text-[11px] text-gray-500 font-mono truncate">{u.email}</div></div>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
